import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import mindPlanAiImg from '../assets/mindplan_ai.jpg';

export default function MindPlanAI() {
  return (
    <div className="min-h-screen bg-violet-50/50 text-slate-900 overflow-hidden relative">
      <div className="relative z-50">
        <Navbar forceOpaque={true} />
      </div>

      <div className="relative pt-32 pb-20 px-6 lg:px-16 min-h-screen flex items-center">
        {/* Background elements */}
        <div className="absolute top-1/4 -right-20 w-[500px] h-[500px] bg-fuchsia-300/40 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-1/4 -left-20 w-[600px] h-[600px] bg-violet-300/40 rounded-full blur-[150px] pointer-events-none" />

        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center z-10">
          
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-700 text-sm font-semibold mb-8">
              <span className="w-2 h-2 rounded-full bg-violet-600 animate-pulse"></span>
              Version 1.0.0 Initial Release 🚀
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-extrabold mb-6 font-syne leading-[1.15] text-slate-900">
              Your Story, <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-fuchsia-600">Your Tasks.</span>
            </h1>

            <p className="text-slate-600 text-lg lg:text-xl mb-10 max-w-xl font-inter leading-relaxed">
              Describe your day and let AI build your perfect schedule. Experience seamless offline task management, smart reminders, and an intuitive UI designed to elevate your productivity.
            </p>

            <div className="space-y-4 mb-10">
              {[
                'AI-powered task planning and smart scheduling',
                'Smart reminders and notifications',
                'Task prioritization and progress tracking',
                'Secure offline support with Firebase Authentication'
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-3 text-slate-700 font-inter">
                  <CheckCircle2 className="text-violet-600" size={20} />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <a href="https://play.google.com/store/apps/details?id=com.mindplanai.kall&hl=en_IN" target="_blank" rel="noopener noreferrer">
                <button className="px-8 py-4 rounded-full bg-violet-600 text-white font-bold font-syne hover:bg-violet-700 transition-colors shadow-lg hover:shadow-violet-500/25">
                  Get it on Google Play
                </button>
              </a>
              <button className="px-8 py-4 rounded-full border border-slate-300 text-slate-700 hover:bg-white transition-colors font-syne font-semibold flex items-center gap-2">
                Explore Features
              </button>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-slate-200/50 bg-white p-2">
              <img src={mindPlanAiImg} alt="MindPlan AI Mobile App" className="w-full h-auto object-cover rounded-[2rem]" />
            </div>
            
            <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-[radial-gradient(circle,rgba(139,92,246,0.2)_2px,transparent_2px)] bg-[length:12px_12px] opacity-60"></div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
